import React from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type {
  ForecastConfig,
  MetricOption,
  TrendDisplayUnit,
  TrendGrain,
  TrendPoint,
  TrendWeightUnit,
} from './types';
import { buildForecast, hasMinimumProjectionData } from './forecast';
import {
  ANALYSIS_PREVIOUS_PALETTE,
  addCopMetricColumn,
  aggregateTrend,
  convertWeight,
  formatMetric,
  getCurrencyUnitOptions,
  getTrendUnitCategory,
  getUnitOptionsByCategory,
} from './utils';

type TrendRowProps = {
  records: Array<Record<string, unknown>>;
  metricOptions: MetricOption[];
  dateAccessor: string;
  loading?: boolean;
  metricKey: string;
  unit: TrendDisplayUnit;
  grain: TrendGrain;
  forecastConfig: ForecastConfig;
  exchangeRateAccessor?: string;
  precomputedPoints?: TrendPoint[];
  onMetricChange: (metricKey: string) => void;
  onUnitChange: (unit: TrendDisplayUnit) => void;
  onGrainChange: (grain: TrendGrain) => void;
  drillDepth?: number;
  chartRef?: React.RefObject<HTMLDivElement | null>;
};

export function TrendRow({
  records,
  metricOptions,
  dateAccessor,
  loading,
  metricKey,
  unit,
  grain,
  forecastConfig,
  exchangeRateAccessor,
  precomputedPoints,
  onMetricChange,
  onUnitChange,
  onGrainChange,
  drillDepth = 0,
  chartRef,
}: TrendRowProps) {
  const selectedMetric = metricOptions.find(option => option.key === metricKey);
  const unitCategory = React.useMemo(
    () => getTrendUnitCategory(selectedMetric?.label ?? metricKey),
    [selectedMetric?.label, metricKey]
  );
  const unitOptions = React.useMemo(() => {
    if (unitCategory === 'currency') {
      return getCurrencyUnitOptions(Boolean(exchangeRateAccessor));
    }
    return getUnitOptionsByCategory(unitCategory);
  }, [unitCategory, exchangeRateAccessor]);
  const displayUnit = unitOptions.includes(unit) ? unit : unitOptions[0];

  const trendAggregationInput = React.useMemo(() => {
    if (unitCategory !== 'currency' || displayUnit !== 'COP' || !exchangeRateAccessor) {
      return { records, metricKey };
    }
    return addCopMetricColumn(records, metricKey, exchangeRateAccessor);
  }, [records, metricKey, unitCategory, displayUnit, exchangeRateAccessor]);

  const rawHistory = React.useMemo(() => {
    if (precomputedPoints) return precomputedPoints;
    return aggregateTrend(
      trendAggregationInput.records,
      dateAccessor,
      trendAggregationInput.metricKey,
      grain
    );
  }, [
    precomputedPoints,
    trendAggregationInput.records,
    trendAggregationInput.metricKey,
    dateAccessor,
    grain,
  ]);

  const history = React.useMemo(() => {
    return rawHistory.map(point => {
      if (unitCategory === 'weight') {
        return {
          ...point,
          metricValue: convertWeight(point.metricValue, 'KG', displayUnit as TrendWeightUnit),
        };
      }
      return point;
    });
  }, [rawHistory, unitCategory, displayUnit]);
  const data = React.useMemo(
    () => buildForecast(history, forecastConfig, grain),
    [history, forecastConfig, grain]
  );
  const canProject = React.useMemo(() => hasMinimumProjectionData(history), [history]);
  const hasProjection = data.some(point => point.isFuture);

  const summary = React.useMemo(() => {
    const total = history.reduce((acc, point) => acc + point.metricValue, 0);
    const totalCount = history.reduce((acc, point) => acc + point.count, 0);
    const average = history.length ? total / history.length : 0;
    return { total, totalCount, average };
  }, [history]);

  const grainLabel =
    grain === 'day' ? 'día' : grain === 'month' ? 'mes' : grain === 'quarter' ? 'trimestre' : 'año';

  return (
    <article className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <select
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700"
          value={metricKey}
          onChange={event => onMetricChange(event.target.value)}
        >
          {metricOptions.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700"
          value={grain}
          onChange={event => onGrainChange(event.target.value as TrendGrain)}
        >
          <option value="day">Día</option>
          <option value="month">Mes</option>
          <option value="quarter">Trimestre</option>
          <option value="year">Año</option>
        </select>
        <select
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700"
          value={displayUnit}
          onChange={event => onUnitChange(event.target.value as TrendDisplayUnit)}
        >
          {unitOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-primary/10 bg-primary/10 px-4 py-3 text-center">
          <p className="text-[2rem] font-bold leading-none text-primary">
            {formatMetric(summary.total)}
          </p>
          <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
            Total {selectedMetric?.label ?? 'Metrica'} ({displayUnit}) todos
          </span>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-center">
          <p className="text-[2rem] font-bold leading-none text-gray-900">
            {summary.totalCount.toLocaleString('es-CO')}
          </p>
          <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
            Registros en todos
          </span>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-center">
          <p className="text-[2rem] font-bold leading-none text-gray-900">
            {formatMetric(summary.average)}
          </p>
          <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
            Promedio por {grainLabel}
          </span>
        </div>
      </div>

      <div ref={chartRef} className="rounded-xl border border-gray-200 bg-white px-4 py-4">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-[1.4rem] font-semibold leading-tight text-gray-900">
              Tendencia Temporal
            </h3>
            <p className="text-sm text-gray-500">
              {selectedMetric?.label ?? 'Metrica'} ({displayUnit}) por {grainLabel}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-right">
            <div>
              <p className="text-base font-bold text-primary">{formatMetric(summary.total)}</p>
              <span className="text-xs font-medium text-gray-500">Total</span>
            </div>
            <div>
              <p className="text-base font-bold text-gray-900">
                {summary.totalCount.toLocaleString('es-CO')}
              </p>
              <span className="text-xs font-medium text-gray-500">Registros</span>
            </div>
            <div>
              <p className="text-base font-bold text-gray-900">{formatMetric(summary.average)}</p>
              <span className="text-xs font-medium text-gray-500">Promedio</span>
            </div>
            {drillDepth > 0 ? (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                Drill-down ({drillDepth})
              </span>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className="flex h-72 items-center justify-center text-sm text-gray-500">
            <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
            <span className="ml-2">Cargando tendencia...</span>
          </div>
        ) : data.length === 0 ? (
          <div className="flex h-72 items-center justify-center text-sm text-gray-500">
            No hay datos suficientes para mostrar tendencia.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 24 }}>
                  <defs>
                    <linearGradient id="trendMetricGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={ANALYSIS_PREVIOUS_PALETTE[0]}
                        stopOpacity={0.28}
                      />
                      <stop
                        offset="95%"
                        stopColor={ANALYSIS_PREVIOUS_PALETTE[0]}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={value => formatMetric(Number(value))}
                  />
                  <Tooltip
                    formatter={(value: number, key: string) => {
                      const labels: Record<string, string> = {
                        metricValue: 'Real',
                        forecast: 'Proyeccion',
                        upper: 'Escenario alza',
                        lower: 'Escenario baja',
                      };
                      return [
                        `${formatMetric(value)} ${displayUnit}`,
                        labels[key] ?? selectedMetric?.label ?? 'Valor',
                      ];
                    }}
                    labelFormatter={label => `Periodo: ${label}`}
                  />
                  {hasProjection ? (
                    <Area
                      type="monotone"
                      dataKey="upper"
                      stroke="none"
                      fill={ANALYSIS_PREVIOUS_PALETTE[0]}
                      fillOpacity={0.08}
                    />
                  ) : null}
                  {hasProjection ? (
                    <Area
                      type="monotone"
                      dataKey="lower"
                      stroke="none"
                      fill="#ffffff"
                      fillOpacity={1}
                    />
                  ) : null}
                  <Area
                    type="monotone"
                    dataKey="metricValue"
                    stroke="none"
                    fill="url(#trendMetricGradient)"
                    fillOpacity={1}
                  />
                  <Line
                    type="monotone"
                    dataKey="metricValue"
                    stroke={ANALYSIS_PREVIOUS_PALETTE[0]}
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  {hasProjection ? (
                    <Line
                      type="monotone"
                      dataKey="forecast"
                      stroke={ANALYSIS_PREVIOUS_PALETTE[2]}
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ) : null}
                  {hasProjection ? (
                    <Line
                      type="monotone"
                      dataKey="upper"
                      stroke={ANALYSIS_PREVIOUS_PALETTE[4]}
                      strokeWidth={1.5}
                      strokeDasharray="3 3"
                      dot={false}
                    />
                  ) : null}
                  {hasProjection ? (
                    <Line
                      type="monotone"
                      dataKey="lower"
                      stroke={ANALYSIS_PREVIOUS_PALETTE[5]}
                      strokeWidth={1.5}
                      strokeDasharray="3 3"
                      dot={false}
                    />
                  ) : null}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-[#1f4bd6]" /> Real
              </span>
              {hasProjection ? (
                <span className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#f59e0b]" /> Proyeccion
                </span>
              ) : null}
              {hasProjection ? (
                <span className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-[#10b981]" /> Rango probable
                </span>
              ) : null}
            </div>
            {!canProject ? (
              <p className="text-xs text-amber-700">
                No hay suficiente data para proyeccion: se requieren minimo 6 puntos y 6 meses
                calendario.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
}
